import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserData } from 'app/shared/service/UserService';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Service that powers the "Link Reference Materials" dialog.
 * Pulls the app list from the same endpoint the App Library dashboard uses,
 * then dispatches per-app content fetches to the existing app-specific APIs
 * (practice sets / exercises / missions / lessons).
 *
 * Kept separate from TrainingMaterialService so the linking flow stays
 * independent of the training-material domain.
 */

export interface LinkableApp {
  id: string;                // game id from getApps
  title: string;             // normalized display title
  iconSrc: string;           // path to icon asset
  itemLabel: string;         // column header in dialog (Practice Set / Exercise Name / Lesson Title / Mission Title)
  showFolder: boolean;       // whether to render a Folder column
  multiplePracticeSets: boolean;
  isActive: boolean;         // mirrors game.isActive from getApps; consumers filter on this
}

export interface LinkableItem {
  id: string;
  name: string;
  folder?: string;
  raw?: any;                 // original record in case caller needs it
}

export interface LinkableItemsQuery {
  search?: string;
  pageNo?: number;           // used only by VECTRA (server pagination); otherwise ignored
  pageSize?: number;
  sortDir?: 'asc' | 'desc';
}

export interface LinkableItemsResponse {
  items: LinkableItem[];
  total: number;
}

// Icon mapping — mirrors training-material.component.ts so display is identical
// across the app-library icon row and the linking dialog sidebar.
const ICON_MAP: { [key: string]: string } = {
  'Memorization': 'assets/img/Memorization.png',
  'WORD JUMBLE': 'assets/img/Word_Game_Logo.png',
  'Word Jumble': 'assets/img/Word_Game_Logo.png',
  'Word Game': 'assets/img/Word_Game_Logo.png',
  'Match': 'assets/img/Match_Game_Logo.png',
  'Match Game': 'assets/img/Match_Game_Logo.png',
  'MATCH GAME': 'assets/img/Match_Game_Logo.png',
  'ERAM Keyboard': 'assets/img/ERAM_Keyboard_Logo.png',
  'VECTRA': 'assets/img/vectra-white.png',
  'Vectra': 'assets/img/vectra-white.png',
  'STARS Keyboard': 'assets/img/STARS_keyboard.png',
  'ATC Phraseology': 'assets/img/ATC_Phraseology.png',
  'Phraseology': 'assets/img/ATC_Phraseology.png',
  'Advance Vectoring': 'assets/img/adv_vec.png',
  'Advanced Vectoring': 'assets/img/adv_vec.png',
  'Advanced Vectoring Game': 'assets/img/adv_vec.png',
  'Basic Vectoring': 'assets/img/basic.png',
  'Vectoring Game': 'assets/img/basic.png',
  'Basic Vectoring Game': 'assets/img/basic.png',
  'Vectoring': 'assets/img/adv_vec.png'
};

const DEFAULT_ICON = 'assets/img/WG-1.jpg';

// Single source of truth for app-name normalization. Mirrors what
// AppDashboardComponent does: backend ships titles like "Vectoring Game" /
// "Advanced Vectoring Game" but the rest of the UI uses "Basic Vectoring" /
// "Advanced Vectoring". Anywhere a raw backend title leaks into the UI
// (linkable-apps fetch, /approved's linkedAppName, etc.) should run through
// this helper before display.
export function normalizeAppTitle(raw: string | null | undefined): string {
  const title = (raw || '').trim();
  if (!title) return '';
  if (title === 'Vectoring Game') return 'Basic Vectoring';
  if (title === 'Advanced Vectoring Game') return 'Advanced Vectoring';
  if (title === 'Word Jumble') return 'WORD JUMBLE';
  if (title === 'Match Game' || title === 'MATCH GAME') return 'Match';
  return title;
}

@Injectable({ providedIn: 'root' })
export class LinkableAppsService {
  private readonly base = `${(UserData.backend_url || '').replace(/\/+$/, '')}/v1`;

  constructor(private http: HttpClient) { }

  /**
   * Fetches the full list of apps from the organization — NO permission filter.
   * Normalizes titles the same way AppDashboardComponent does
   * (Vectoring Game → Basic Vectoring, Advanced Vectoring Game → Advanced Vectoring,
   * uppercases Word Jumble and Match Game).
   */
  getApps(): Observable<LinkableApp[]> {
    const orgId = this.getOrgId();
    const url = `${this.base}/organizations/${orgId}/getApps`;
    return this.http.get<any>(url).pipe(
      map(res => {
        const games: any[] = res?.Games || [];
        return games.map(g => this.normalizeApp(g)).filter(a => !!a.id);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Fetches the items (practice sets / exercises / missions / lessons) for a given app.
   * Dispatches by normalized title; response shapes are flattened to {id, name, folder?}.
   */
  getAppItems(app: LinkableApp, query: LinkableItemsQuery = {}): Observable<LinkableItemsResponse> {
    switch (app.title) {
      case 'VECTRA':
        return this.fetchVectraExercises(query);
      case 'Basic Vectoring':
        return this.fetchArrayEndpoint(`${this.base}/basic-vectoring-lesson`, 'title');
      case 'Advanced Vectoring':
        return this.fetchArrayEndpoint(`${this.base}/advanced-vectoring-mission`, 'title');
      case 'ERAM Keyboard':
        return this.fetchArrayEndpoint(`${this.base}/eram-lesson`, 'title');
      case 'STARS Keyboard':
        return this.fetchArrayEndpoint(`${this.base}/STARS-lesson`, 'title');
      default:
        // Memorization / WORD JUMBLE / MATCH GAME / ATC Phraseology / custom apps
        return this.fetchPracticeSetsForGame(app.id);
    }
  }

  getIconSrc(title: string): string {
    return ICON_MAP[title] || DEFAULT_ICON;
  }

  /* ── internal helpers ─────────────────────────────────────────────── */

  private getOrgId(): string {
    const fromUserData = (UserData as any)?.organizationId;
    if (fromUserData) return fromUserData;
    const fromStorage = localStorage.getItem('organizationId');
    return fromStorage || 'noOrg';
  }

  private normalizeApp(game: any): LinkableApp {
    const title = normalizeAppTitle(game?.name);
    const itemMeta = this.itemMetaForTitle(title);

    return {
      id: game?.id || '',
      title,
      iconSrc: this.getIconSrc(title),
      itemLabel: itemMeta.label,
      showFolder: itemMeta.showFolder,
      multiplePracticeSets: !!game?.multiplePracticeSetsEnabled,
      isActive: game?.isActive === true
    };
  }

  /**
   * Convenience: returns the set of active app IDs for the current org.
   * Consumers that just need to know "is this id linkable now?" can use
   * this without re-running normalisation. Returns an empty Set on error.
   */
  getActiveAppIds(): Observable<Set<string>> {
    return this.getApps().pipe(
      map(apps => new Set(apps.filter(a => a.isActive).map(a => a.id))),
      catchError(() => of(new Set<string>()))
    );
  }

  private itemMetaForTitle(title: string): { label: string; showFolder: boolean } {
    switch (title) {
      case 'VECTRA':
        return { label: 'Exercise Name', showFolder: false };
      case 'Basic Vectoring':
      case 'Advanced Vectoring':
      case 'ERAM Keyboard':
      case 'STARS Keyboard':
        return { label: 'Lesson / Mission', showFolder: false };
      default:
        return { label: 'Practice Set', showFolder: true };
    }
  }

  /**
   * VECTRA uses server-side pagination + search + sort.
   */
  private fetchVectraExercises(q: LinkableItemsQuery): Observable<LinkableItemsResponse> {
    const url = `${this.base}/atc/excercise`;
    const params = new HttpParams()
      .set('pageNo', String(q.pageNo ?? 0))
      .set('pageSize', String(q.pageSize ?? 1000))
      .set('sortField', 'exerciseName')
      .set('sortDirection', q.sortDir ?? 'asc')
      .set('searchField', q.search ?? '');
    return this.http.get<any>(url, { params }).pipe(
      map(res => {
        const records: any[] = res?.record || [];
        return {
          items: records.map(r => ({
            id: r?.id,
            name: r?.exerciseName,
            folder: r?.airportName,
            raw: r
          })),
          total: res?.length ?? records.length
        };
      }),
      catchError(() => of({ items: [], total: 0 }))
    );
  }

  /**
   * Applies to endpoints that return a flat array (lessons / missions).
   */
  private fetchArrayEndpoint(url: string, nameField: string): Observable<LinkableItemsResponse> {
    return this.http.get<any>(url).pipe(
      map(res => {
        const records: any[] = Array.isArray(res) ? res : (res?.record || []);
        return {
          items: records.map(r => ({
            id: r?.id,
            name: r?.[nameField] || r?.title || r?.name || '',
            raw: r
          })),
          total: records.length
        };
      }),
      catchError(() => of({ items: [], total: 0 }))
    );
  }

  /**
   * For games served by content-management/practicesetsUI, the response is a
   * tree of folders with nested practicesets. Flatten to items with folder name.
   */
  private fetchPracticeSetsForGame(gameId: string): Observable<LinkableItemsResponse> {
    const orgId = this.getOrgId();
    const url = `${this.base}/organizations/${orgId}/games/${gameId}/practicesetsUI`;
    return this.http.get<any>(url).pipe(
      map(res => {
        const items = this.flattenPracticeSetsResponse(res);
        return { items, total: items.length };
      }),
      catchError(() => of({ items: [], total: 0 }))
    );
  }

  /**
   * practicesetsUI returns `{ Folders: [...], PracticeSets: [...] }` where
   * each folder has a nested `practiceSets` / `childFolders` tree and the
   * top-level `PracticeSets` are orphaned (no folder) sets. See
   * DashboardService.setPSetData / setFolderData for the field names.
   * We flatten everything to a single list of leaves with folder name attached.
   */
  private flattenPracticeSetsResponse(res: any): LinkableItem[] {
    if (!res) return [];
    const out: LinkableItem[] = [];
    const seen = new Set<string>();

    const pushItem = (ps: any, folderName?: string) => {
      if (!ps) return;
      const id = ps.id ?? ps.setID ?? ps.practiceSetId ?? ps.practicesetId;
      const name = ps.name ?? ps.practiceSetName ?? ps.practicesetName ?? ps.title;
      if (!id || !name) return;
      const key = String(id);
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        id: key,
        name: String(name),
        folder: folderName || ps.folderName || 'None',
        raw: ps
      });
    };

    const visitFolder = (folder: any, parentFolderName?: string) => {
      if (!folder) return;
      const fName = folder.name || folder.practiceGroupName || folder.folderName || parentFolderName;
      // Nested practice sets inside this folder
      const nested = folder.practiceSets || folder.practicesets || [];
      if (Array.isArray(nested)) {
        nested.forEach((ps: any) => pushItem(ps, fName));
      }
      // Recurse into child folders
      const children = folder.childFolders || folder.children || [];
      if (Array.isArray(children)) {
        children.forEach((child: any) => visitFolder(child, fName));
      }
    };

    // 1. Folders with nested practice sets
    const folders = res.Folders || res.folders || [];
    if (Array.isArray(folders)) {
      folders.forEach((f: any) => visitFolder(f));
    }

    // 2. Orphan practice sets at root (no folder)
    const rootSets = res.PracticeSets || res.practiceSets || res.practicesets || [];
    if (Array.isArray(rootSets)) {
      rootSets.forEach((ps: any) => pushItem(ps, ps.folderName || 'None'));
    }

    // 3. Fallback: if response itself is a flat array of practice sets
    if (Array.isArray(res)) {
      res.forEach((ps: any) => pushItem(ps));
    }

    return out;
  }
}
